/**
 * SalesAllocationFormDialog — New / Edit / Copy for Sales Control › Sales Allocation.
 * Converted 1:1 from Access `frmCtrlSalesQrySaleAlcAdd` (table
 * `public.sales_allocation_scs`, PK `sales_allocation_scs_id`). Two-column Detail
 * (input Left 2496 → col 1, Left 9432 → col 2); rows by Top. Bold gutter section
 * headers split the body: General info · Outbound · Customer inbound · Allocation.
 *
 * New/Edit/Copy contract (from main form `frmCtrlSalesMainSubFormSaleAlc`):
 *  - new:  blank; sale_contract + consignment_contract enabled.
 *  - edit: set id; both contract combos DISABLED; hydrate from row. Split logic:
 *          sale_contract starts with 'S' → sale_contract set, consignment blank;
 *          starts with 'NPE' → consignment set, sale_contract blank; else both =
 *          the stored value. gdn_or_rdn 'RDN' → virtual_wh = receipt_no,
 *          receipt_no blank; 'GDN' → receipt_no set; else both. contract_quantity
 *          from DO snapshot. contract_no_of_bag = null.
 *  - copy: like edit BUT contract combos ENABLED and no id (new record);
 *          contract_no_of_bag = null.
 *
 * btnSave_Click maps: contract_no = sale_contract || consignment_contract;
 * receipt_no/gdn_or_rdn = receipt_no→GDN else virtual_wh→RDN. errCheck:
 *   1 neither contract, 2 both contracts, 3 both receipt+virtual_wh,
 *   4 seal_no already exists (New only). Cascades: contract→client (display),
 *   DO→contract_quantity/from_wh/to_wh, receipt/virtual_wh→export bags+qty
 *   (+allocating when terms CIF/FOB/FOT). All resolved through BE snapshot
 *   endpoints (see saleAllocationApi — TODO BE).
 */
import { useEffect, useRef, useState } from 'react'
import { TEXT_TRUNCATE } from '../../theme/responsive'
import {
  Alert, Autocomplete, Box, Button, CircularProgress, createFilterOptions, Dialog, DialogContent,
  DialogTitle, Stack, TextField, Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

import {
  type SaleAlcLookupItem,
  type SaleAllocationForm,
  type SaleAllocationWrite,
  useLazyGetSaleAllocationFormQuery,
  useCreateSaleAllocationMutation,
  useUpdateSaleAllocationMutation,
  useLazyCheckSaleAllocationSealQuery,
  useLazyGetSaleAllocationDoSnapshotQuery,
  useLazyGetSaleAllocationReceiptSnapshotQuery,
  useSaleAlcLookupSaleContractsQuery,
  useSaleAlcLookupConsignmentsQuery,
  useSaleAlcLookupDeliveryOrdersQuery,
  useSaleAlcLookupWarehousesQuery,
  useSaleAlcLookupReceiptNosQuery,
  useSaleAlcLookupVirtualWarehousesQuery,
} from '../../store/api/saleControlApi'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'

type Mode = 'new' | 'edit' | 'copy'

interface Props {
  open: boolean
  mode: Mode
  /** PK `sales_allocation_scs_id` of the selected row (Edit/Copy). */
  saleAllocationId?: number | null
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

interface Draft {
  saleContract: string
  consignmentContract: string
  client: string            // readonly snapshot (.column(2) of contract)
  deliveryTerms: string     // readonly calc (Access contract_terms =DLookup delivery_terms)
  deliveryOrder: string
  bookingNo: string         // readonly calc (Access booking_no =DLookup by delivery_order)
  contractNoOfBag: string   // readonly, always blank on open per main form
  contractQuantity: string  // readonly snapshot from DO
  mDate: string
  mTruckNo: string
  mNetWeight: string
  fromWarehouse: string
  toWarehouse: string
  containerNo: string
  sealNo: string
  noBag: string
  tareWeight: string
  tareCartonPallet: string
  tareCont: string
  maxGrossWeight: string
  receiptNo: string
  virtualWh: string
  exportNoOfBag: string     // readonly snapshot
  exportQuantity: string    // readonly snapshot
  netWeightAtBuyer: string
  deductionAtBuyerKg: string
  deductionAtBuyerVnd: string
  allocatingBag: string
  allocatingQuantity: string
  note: string
}

const EMPTY: Draft = {
  saleContract: '', consignmentContract: '', client: '', deliveryTerms: '', deliveryOrder: '',
  bookingNo: '', contractNoOfBag: '', contractQuantity: '', mDate: '', mTruckNo: '', mNetWeight: '',
  fromWarehouse: '', toWarehouse: '', containerNo: '', sealNo: '', noBag: '',
  tareWeight: '', tareCartonPallet: '', tareCont: '', maxGrossWeight: '',
  receiptNo: '', virtualWh: '', exportNoOfBag: '', exportQuantity: '',
  netWeightAtBuyer: '', deductionAtBuyerKg: '', deductionAtBuyerVnd: '',
  allocatingBag: '', allocatingQuantity: '', note: '',
}

const SM = 'small' as const
const compactInputSx: SxProps<Theme> = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}
const titleBtnSx: SxProps<Theme> = { minWidth: 0, px: 1.25, py: 0.3, fontSize: '0.78rem', textTransform: 'none' }
const sectionTitleSx: SxProps<Theme> = {
  fontWeight: 700, fontSize: '0.8125rem', color: 'primary.main',
  mt: 1.5, mb: 0.75, pb: 0.25, borderBottom: '1px solid', borderColor: 'divider',
}
const hintSx: SxProps<Theme> = { fontSize: '0.72rem', color: 'text.secondary', fontStyle: 'italic' }
const grid2Sx: SxProps<Theme> = {
  display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
  columnGap: 1.5, rowGap: 1.25, alignItems: 'start',
}
const readonlyFieldSx = {
  '& .MuiInputBase-root': { bgcolor: (t: Theme) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5') },
  '& .MuiInputBase-input': { color: (t: Theme) => t.palette.text.secondary, fontWeight: 600 },
}
const numProps = { style: { textAlign: 'right' as const }, inputMode: 'decimal' as const }

function parseNum(v: string): number | null {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}
function fmtStd(v: unknown): string {
  if (v == null || v === '') return ''
  const n = typeof v === 'number' ? v : parseNum(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
/** 0-decimal Standard (Access Format '0' / '* #,##0'): tare_*, max_gross_weight, net_weight_at_buyer. */
function fmtInt(v: unknown): string {
  if (v == null || v === '') return ''
  const n = typeof v === 'number' ? v : parseNum(String(v))
  if (n == null) return ''
  return Math.round(n).toLocaleString('en-US', { maximumFractionDigits: 0 })
}
const str = (v: unknown): string => (v == null ? '' : String(v))

const fullLabel = (o: SaleAlcLookupItem): string =>
  (o.value === o.label ? String(o.label ?? '') : `${o.value} — ${o.label}`)

// Client-side search khớp cả CODE lẫn NAME ("<CODE> — <NAME>"), bỏ qua hoa/thường +
// khoảng trắng thừa. Dùng cho MỌI combo → gõ là list lọc ngay (không phụ thuộc server).
const comboFilter = createFilterOptions<SaleAlcLookupItem>({
  stringify: (o) => fullLabel(o),
  trim: true,
})

/** Inject the current value into options so the chip shows even before list load. */
function withCurrent(options: SaleAlcLookupItem[], value: string): SaleAlcLookupItem | null {
  if (!value) return null
  return options.find((o) => o.value === value) ?? { value, label: value }
}

function Combo(props: {
  label: string
  value: string
  options: SaleAlcLookupItem[]
  loading?: boolean
  disabled?: boolean
  required?: boolean
  error?: boolean
  helperText?: string
  /** Access bound-column behaviour: field shows only the code after pick; dropdown still shows "CODE — NAME". */
  codeOnly?: boolean
  onInputChange?: (q: string) => void
  onPick: (value: string, item: SaleAlcLookupItem | null) => void
}) {
  const { label, value, options, loading, disabled, required, error, helperText, codeOnly, onInputChange, onPick } = props
  const sel = withCurrent(options, value)
  const merged = sel && !options.some((o) => o.value === sel.value) ? [sel, ...options] : options
  // Dedupe by value — lookups can return duplicate codes (e.g. 'LDC'); a duplicate
  // value collapses to the same Autocomplete option key → React duplicate-key warning.
  const seen = new Set<string>()
  const opts = merged.filter((o) => {
    const k = String(o.value)
    if (seen.has(k)) return false
    seen.add(k)
    return true
  })
  return (
    <Autocomplete
      size={SM}
      fullWidth
      disabled={disabled}
      options={opts}
      value={sel}
      loading={loading}
      // Strict: KHÔNG freeSolo → chỉ nhận giá trị trong list; gõ chuỗi lạ rồi blur sẽ
      // tự xoá (clearOnBlur) về giá trị đã chọn. autoHighlight + Enter chọn match đầu.
      autoHighlight
      clearOnBlur
      handleHomeEndKeys
      selectOnFocus
      blurOnSelect
      onChange={(_e, v) => onPick(v?.value ?? '', v)}
      onInputChange={onInputChange ? (_e, v, reason) => { if (reason === 'input') onInputChange(v) } : undefined}
      getOptionLabel={(o) => (!o ? '' : codeOnly ? String(o.value) : fullLabel(o))}
      renderOption={(liProps, o) => <li {...liProps} key={o.value}>{fullLabel(o)}</li>}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      // Client-side filter (code + name) → search chạy ngay trên list đã load.
      filterOptions={comboFilter}
      renderInput={(params) => (
        <TextField {...params} label={required ? `${label} *` : label} error={error} helperText={helperText} />
      )}
    />
  )
}

export default function SalesAllocationFormDialog({ open, mode, saleAllocationId, canEdit, onClose, onSaved }: Props) {
  const snack = useSnackbar()
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [sealExists, setSealExists] = useState(false)
  // Edit/Copy hydrate hits a ~2s view query → show a loading state until the
  // API response lands, then render the fields (avoid a flash of empty inputs).
  const [formLoading, setFormLoading] = useState(false)
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((p) => ({ ...p, [k]: v }))

  // Contract combos are disabled only in Edit (main form: edit sets Enabled=False;
  // copy keeps Enabled=True). Used too for the seal-no uniqueness gate (errCheck 4
  // only fires when sale_contract.Enabled = True, i.e. New/Copy).
  const contractsDisabled = mode === 'edit'

  const [loadForm] = useLazyGetSaleAllocationFormQuery()
  const [createRow, { isLoading: creating }] = useCreateSaleAllocationMutation()
  const [updateRow, { isLoading: updating }] = useUpdateSaleAllocationMutation()
  const [checkSeal] = useLazyCheckSaleAllocationSealQuery()
  const [fetchDoSnapshot] = useLazyGetSaleAllocationDoSnapshotQuery()
  const [fetchReceiptSnapshot] = useLazyGetSaleAllocationReceiptSnapshotQuery()
  const saving = creating || updating

  // Combo lookups — load while dialog open (server-side search via q).
  const skip = { skip: !open }
  const [scQ, setScQ] = useState('')
  const [cnQ, setCnQ] = useState('')
  const [doQ, setDoQ] = useState('')
  const saleContracts = useSaleAlcLookupSaleContractsQuery(scQ || undefined, skip)
  const consignments = useSaleAlcLookupConsignmentsQuery(cnQ || undefined, skip)
  // DO list load theo contract đang chọn (S Contract hoặc Consignment); rỗng → full list.
  const activeContract = (draft.saleContract || draft.consignmentContract).trim()
  const deliveryOrders = useSaleAlcLookupDeliveryOrdersQuery(
    { q: doQ || undefined, contract: activeContract || undefined }, skip,
  )
  const warehouses = useSaleAlcLookupWarehousesQuery(undefined, skip)
  // Receipt No (GDN) + Virtual WH load theo DO đang chọn; chưa chọn DO → full list.
  const doFilter = draft.deliveryOrder.trim() || undefined
  const receiptNos = useSaleAlcLookupReceiptNosQuery({ doNo: doFilter }, skip)
  const virtualWhs = useSaleAlcLookupVirtualWarehousesQuery({ doNo: doFilter }, skip)

  // ── Hydrate on open ───────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setSealExists(false)
    if (mode !== 'new') setFormLoading(true) // show loading immediately for Edit/Copy
    void (async () => {
      if (mode === 'new') {
        setFormLoading(false)
        setDraft({ ...EMPTY, mDate: dayjs().format('YYYY-MM-DD') })
        return
      }
      if (!saleAllocationId) { setFormLoading(false); return }
      const f = await loadForm(saleAllocationId).unwrap().catch(() => null)
      if (cancelled) return
      if (!f) { setFormLoading(false); return }
      const row = f as SaleAllocationForm
      // Contract split (main form): NPE* → consignment; else (S* or other) → sale_contract.
      // Access's else-branch fills BOTH, which trips the mutual-exclusion guard on the
      // web (contractBoth) — so route a single field instead.
      const stored = str(row.sale_contract)
      let saleContract = ''
      let consignmentContract = ''
      if (stored.slice(0, 3) === 'NPE') {
        consignmentContract = stored
      } else {
        saleContract = stored
      }
      // Receipt split (main form): RDN → virtual_wh; else (GDN/empty/unknown) → receipt_no.
      // Access's else-branch fills BOTH → trips receiptBoth on the web; route single.
      const gdnRdn = str(row.gdn_or_rdn).trim().toUpperCase()
      const receipt = str(row.receipt_no)
      let receiptNo = ''
      let virtualWh = ''
      if (gdnRdn === 'RDN') {
        virtualWh = receipt
      } else {
        receiptNo = receipt
      }
      // Access btnCopy does NOT carry seal_no / receipt_no / virtual_wh / export
      // (only btnEdit does) — leave them blank on Copy so a new record isn't created
      // with the source's seal (sealExists) or both receipt fields (receiptBoth).
      const isCopy = mode === 'copy'
      if (isCopy) { receiptNo = ''; virtualWh = '' }
      setDraft({
        ...EMPTY,
        saleContract,
        consignmentContract,
        client: str(row.client),
        deliveryTerms: str(row.contract_terms),
        deliveryOrder: str(row.delivery_order),
        bookingNo: str(row.booking_no),
        contractNoOfBag: '', // main form forces null on open
        contractQuantity: fmtStd(row.contract_quantity),
        mDate: str(row.m_date).slice(0, 10),
        mTruckNo: str(row.m_truck_no),
        mNetWeight: fmtStd(row.m_net_weight),
        fromWarehouse: str(row.from_warehouse),
        toWarehouse: str(row.to_warehouse),
        containerNo: str(row.container_no),
        sealNo: isCopy ? '' : str(row.seal_no),
        noBag: fmtStd(row.number_bags),
        tareWeight: fmtInt(row.tare_weight),
        tareCartonPallet: fmtInt(row.tare_carton_pallet),
        tareCont: fmtInt(row.tare_cont),
        maxGrossWeight: fmtInt(row.max_gross_weight),
        receiptNo,
        virtualWh,
        exportNoOfBag: isCopy ? '' : fmtStd(row.export_no_of_bag),
        exportQuantity: isCopy ? '' : fmtStd(row.export_quantity),
        netWeightAtBuyer: fmtInt(row.net_weight_at_buyer),
        deductionAtBuyerKg: fmtStd(row.deduction_at_buyer_kg),
        deductionAtBuyerVnd: fmtStd(row.deduction_at_buyer_vnd),
        allocatingBag: fmtStd(row.allocating_bag),
        allocatingQuantity: fmtStd(row.allocating_quantity),
        note: str(row.note),
      })
      setFormLoading(false)
    })()
    return () => { cancelled = true }
  }, [open, mode, saleAllocationId, loadForm])

  // ── seal_no uniqueness (seal_no_AfterUpdate + errCheck 4; New/Copy only) ──
  const checkSealNo = (v: string) => {
    const sealNo = v.trim()
    if (contractsDisabled || !sealNo) { setSealExists(false); return }
    void checkSeal({ sealNo, excludeId: saleAllocationId ?? undefined }).unwrap()
      .then((r) => setSealExists(r.exists)).catch(() => setSealExists(false))
  }

  // ── delivery_order_AfterUpdate: contract_quantity / from_wh / to_wh ──
  const handleDeliveryOrderPick = (doNo: string) => {
    // Access booking_no is a =DLookup ControlSource keyed on delivery_order — it
    // recomputes when DO changes. The DO snapshot now carries booking_no, so repopulate
    // it from the snapshot (cleared first to avoid a stale value while fetching).
    // Đổi DO → Receipt No / Virtual WH cũ có thể không thuộc DO mới: clear chúng +
    // export phái sinh để user chọn lại từ list đã lọc theo DO.
    setDraft((p) => ({
      ...p, deliveryOrder: doNo, contractNoOfBag: '', bookingNo: '',
      receiptNo: '', virtualWh: '', exportNoOfBag: '', exportQuantity: '',
    }))
    if (!doNo) { set('contractQuantity', ''); return }
    void fetchDoSnapshot(doNo).unwrap()
      .then((r) => setDraft((p) => ({
        ...p,
        contractQuantity: fmtStd(r.contract_quantity),
        fromWarehouse: r.from_warehouse ?? p.fromWarehouse,
        toWarehouse: r.to_warehouse ?? p.toWarehouse,
        bookingNo: r.booking_no ?? '',
      })))
      .catch(() => undefined)
  }

  // ── receipt_no / virtual_wh AfterUpdate: export bags+qty (+ allocating) ──
  const applyReceiptSnapshot = (kind: 'gdn' | 'rdn', value: string, scOverride?: string) => {
    if (!value) { set('exportNoOfBag', ''); set('exportQuantity', ''); return }
    void fetchReceiptSnapshot({ kind, value, saleContract: (scOverride ?? draft.saleContract) || undefined }).unwrap()
      .then((r) => setDraft((p) => ({
        ...p,
        exportNoOfBag: fmtStd(r.export_no_of_bag),
        exportQuantity: fmtStd(r.export_quantity),
        // Only on New/Copy (Enabled=True) BE returns allocating values for CIF/FOB/FOT.
        allocatingBag: r.allocating_bag != null ? fmtStd(r.allocating_bag) : p.allocatingBag,
        allocatingQuantity: r.allocating_quantity != null ? fmtStd(r.allocating_quantity) : p.allocatingQuantity,
      })))
      .catch(() => undefined)
  }
  // sale_contract_AfterUpdate: set client (col 2) + terms, clear consignment; and on
  // New/Copy recompute allocating for CIF/FOB/FOT when a receipt/virtual is already picked.
  const handleSaleContractPick = (v: string, item: SaleAlcLookupItem | null) => {
    setDraft((p) => ({
      ...p, saleContract: v,
      consignmentContract: v ? '' : p.consignmentContract,
      client: v ? (item?.label ?? '') : '',
      deliveryTerms: v ? (item?.terms ?? '') : '',
      // Đổi contract → DO cũ có thể không thuộc contract mới: clear DO + field phái sinh.
      deliveryOrder: '', contractNoOfBag: '', bookingNo: '', contractQuantity: '',
    }))
    if (!contractsDisabled && v) {
      if (draft.receiptNo) applyReceiptSnapshot('gdn', draft.receiptNo, v)
      else if (draft.virtualWh) applyReceiptSnapshot('rdn', draft.virtualWh, v)
    }
  }
  const handleReceiptPick = (v: string) => {
    // GDN side: picking receipt_no clears virtual_wh.
    setDraft((p) => ({ ...p, receiptNo: v, virtualWh: v ? '' : p.virtualWh }))
    applyReceiptSnapshot('gdn', v)
  }
  const handleVirtualWhPick = (v: string) => {
    // RDN side: picking virtual_wh clears receipt_no.
    setDraft((p) => ({ ...p, virtualWh: v, receiptNo: v ? '' : p.receiptNo }))
    applyReceiptSnapshot('rdn', v)
  }

  // ── Auto-select "chỉ 1 item" (New/Copy only; Edit đã có sẵn giá trị record) ──
  // Refs khoá theo parent-value đã xử lý → auto-fill 1 LẦN mỗi lần parent đổi (user
  // xoá tay sau đó sẽ KHÔNG bị fill lại).
  const autoDoRef = useRef<string>('')
  const autoReceiptRef = useRef<string>('')

  // Chọn S Contract / Consignment → nếu DO đã lọc chỉ còn đúng 1 item → load luôn.
  useEffect(() => {
    if (mode !== 'new') return
    if (!activeContract) { autoDoRef.current = ''; return }
    if (deliveryOrders.isFetching) return
    if (autoDoRef.current === activeContract) return
    autoDoRef.current = activeContract // đánh dấu đã xử lý contract này
    const items = deliveryOrders.data?.items ?? []
    if (items.length === 1 && !draft.deliveryOrder) handleDeliveryOrderPick(items[0].value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeContract, deliveryOrders.isFetching, deliveryOrders.data, mode])

  // Chọn DO → nếu Receipt No / Virtual WH đã lọc chỉ còn đúng 1 item (và chỉ 1 trong
  // 2 nguồn có hàng) → load luôn vào field tương ứng.
  useEffect(() => {
    if (mode !== 'new') return
    if (!draft.deliveryOrder) { autoReceiptRef.current = ''; return }
    if (receiptNos.isFetching || virtualWhs.isFetching) return
    if (autoReceiptRef.current === draft.deliveryOrder) return
    autoReceiptRef.current = draft.deliveryOrder // đánh dấu đã xử lý DO này
    if (draft.receiptNo || draft.virtualWh) return // đã có pick (vd Copy giữ, hoặc user chọn)
    const rc = receiptNos.data?.items ?? []
    const vc = virtualWhs.data?.items ?? []
    if (rc.length === 1 && vc.length === 0) handleReceiptPick(rc[0].value)
    else if (vc.length === 1 && rc.length === 0) handleVirtualWhPick(vc[0].value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.deliveryOrder, receiptNos.isFetching, virtualWhs.isFetching, receiptNos.data, virtualWhs.data, mode])

  // ── btnSave errCheck 1/2/3 (+4 seal) gates ──
  const contractMissing = !draft.saleContract.trim() && !draft.consignmentContract.trim()
  const contractBoth = !!draft.saleContract.trim() && !!draft.consignmentContract.trim()
  const receiptBoth = !!draft.receiptNo.trim() && !!draft.virtualWh.trim()
  const canSave = canEdit && !contractMissing && !contractBoth && !receiptBoth && !sealExists && !saving

  const handleClose = () => { if (!saving) onClose() }

  const handleSave = async () => {
    if (!canSave) return
    // contract_no = sale_contract || consignment_contract (btnSave_Click).
    const contractNo = draft.saleContract.trim() || draft.consignmentContract.trim() || null
    // receipt_no/gdn_or_rdn discriminator (btnSave_Click).
    let receiptNo: string | null = null
    let gdnOrRdn: string | null = null
    if (draft.receiptNo.trim()) { receiptNo = draft.receiptNo.trim(); gdnOrRdn = 'GDN' }
    else if (draft.virtualWh.trim()) { receiptNo = draft.virtualWh.trim(); gdnOrRdn = 'RDN' }
    const payload: SaleAllocationWrite = {
      sale_contract: contractNo,
      delivery_order: draft.deliveryOrder || null,
      m_date: draft.mDate || null,
      from_warehouse: draft.fromWarehouse || null,
      to_warehouse: draft.toWarehouse || null,
      m_truck_no: draft.mTruckNo || null,
      m_net_weight: parseNum(draft.mNetWeight),
      container_no: draft.containerNo || null,
      seal_no: draft.sealNo || null,
      number_bags: parseNum(draft.noBag),
      tare_weight: parseNum(draft.tareWeight),
      tare_carton_pallet: parseNum(draft.tareCartonPallet),
      tare_cont: parseNum(draft.tareCont),
      max_gross_weight: parseNum(draft.maxGrossWeight),
      receipt_no: receiptNo,
      gdn_or_rdn: gdnOrRdn,
      net_weight_at_buyer: parseNum(draft.netWeightAtBuyer),
      deduction_at_buyer_kg: parseNum(draft.deductionAtBuyerKg),
      deduction_at_buyer_vnd: parseNum(draft.deductionAtBuyerVnd),
      allocating_bag: parseNum(draft.allocatingBag),
      allocating_quantity: parseNum(draft.allocatingQuantity),
      note: draft.note || null,
    }
    try {
      if (mode === 'edit' && saleAllocationId) {
        await updateRow({ id: saleAllocationId, body: { ...payload, sales_allocation_scs_id: saleAllocationId } }).unwrap()
        snack.success('Sales Allocation updated')
      } else {
        await createRow(payload).unwrap()
        snack.success('Sales Allocation added')
      }
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Save sales allocation failed'))
    }
  }

  const title = mode === 'new' ? 'New' : mode === 'copy' ? 'Copy' : 'Edit'

  const dateField = (key: keyof Draft, label: string, gc: number | string, gr: number) => (
    <DatePicker
      label={label}
      format="DD-MMM-YYYY"
      value={draft[key] ? dayjs(draft[key] as string) : null}
      onChange={(d) => set(key, (d && d.isValid() ? d.format('YYYY-MM-DD') : '') as Draft[typeof key])}
      slotProps={{ textField: { size: SM, fullWidth: true, InputLabelProps: { shrink: true }, sx: { gridColumn: { sm: gc }, gridRow: { sm: gr } } } }}
    />
  )
  const numField = (key: keyof Draft, label: string, gc: number | string, gr: number, int?: boolean) => (
    <TextField
      label={label} size={SM} fullWidth type="text" value={draft[key] as string}
      onChange={(e) => set(key, e.target.value as Draft[typeof key])}
      onBlur={() => set(key, (int ? fmtInt(draft[key] as string) : fmtStd(draft[key] as string)) as Draft[typeof key])}
      inputProps={numProps}
      sx={{ gridColumn: { sm: gc }, gridRow: { sm: gr } }}
    />
  )
  const textField = (key: keyof Draft, label: string, gc: number | string, gr: number, extra?: object) => (
    <TextField label={label} size={SM} fullWidth value={draft[key] as string}
      onChange={(e) => set(key, e.target.value as Draft[typeof key])}
      sx={{ gridColumn: { sm: gc }, gridRow: { sm: gr } }} {...extra} />
  )
  const roField = (key: keyof Draft, label: string, gc: number | string, gr: number | string, num?: boolean) => (
    <TextField label={label} size={SM} fullWidth value={draft[key] as string}
      InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }}
      inputProps={num ? numProps : undefined}
      sx={{ gridColumn: { sm: gc }, gridRow: { sm: gr }, ...readonlyFieldSx }} />
  )

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      fullWidth
      onClose={(_e, reason) => { if (saving || reason === 'backdropClick') return; handleClose() }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Typography component="span" variant="subtitle1" noWrap title={`${title} — Sales Allocation`} sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700 }}>{title} — Sales Allocation</Typography>
        <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }}>
          <Button type="button" variant="contained" color="success" sx={titleBtnSx} onClick={() => void handleSave()} disabled={!canSave}>
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" sx={titleBtnSx} onClick={handleClose} disabled={saving}>Cancel</Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={compactInputSx}>
        {formLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 8 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          </Box>
        ) : (
        <>
        {!canEdit && <Alert severity="info" sx={{ mb: 1.5 }}>You do not have permission to edit sales allocations.</Alert>}
        {/* btnSave errCheck messages — verbatim from Access VBA (incl. its wording). */}
        {contractMissing && <Alert severity="error" sx={{ mb: 1.5 }}>Please choose S contract or Consignment!</Alert>}
        {contractBoth && <Alert severity="warning" sx={{ mb: 1.5 }}>Please choose only S contract or Consignment!</Alert>}
        {receiptBoth && <Alert severity="warning" sx={{ mb: 1.5 }}>Please choose only Receive No or Virtual Warehouse!</Alert>}

        {/* ── Sale Alc ID (Access `sales_allocation_scs_id`, Top 36, READONLY PK).
            Edit shows the id; New/Copy blank (VBA treats empty as new → insert). ── */}
        <Box sx={{ ...grid2Sx, mb: 1.5 }}>
          <TextField label="Sale Alc ID" size={SM} fullWidth
            value={mode === 'edit' ? String(saleAllocationId ?? '') : ''}
            InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }}
            sx={{ gridColumn: { sm: 1 }, ...readonlyFieldSx }} />
        </Box>

        {/* ── Contracts (Access Top 456–1884; input Left 2496 → col 1, 9432 → col 2) ── */}
        <Box sx={grid2Sx}>
          {/* r1 (Top 456): sale_contract (c1) · consignment (c2) */}
          <Combo label="S Contract" value={draft.saleContract} disabled={contractsDisabled} codeOnly
            options={saleContracts.data?.items ?? []} loading={saleContracts.isFetching} onInputChange={setScQ}
            error={contractBoth || contractMissing}
            onPick={handleSaleContractPick} />
          <Combo label="Consignment" value={draft.consignmentContract} disabled={contractsDisabled} codeOnly
            options={consignments.data?.items ?? []} loading={consignments.isFetching} onInputChange={setCnQ}
            error={contractBoth || contractMissing}
            onPick={(v, item) => setDraft((p) => ({ ...p, consignmentContract: v, saleContract: v ? '' : p.saleContract, client: v ? (item?.label ?? '') : '', deliveryTerms: '', deliveryOrder: '', contractNoOfBag: '', bookingNo: '', contractQuantity: '' }))} />
          {/* r2 (Top 828): hint (c1) */}
          <Typography sx={{ ...hintSx, gridColumn: { sm: 1 }, gridRow: { sm: 2 } }}>* Only choose S contract or Consignment</Typography>
          {/* r3: delivery_terms (c1) · client (c2) — same row per request */}
          {roField('deliveryTerms', 'Delivery Terms', 1, 3)}
          {roField('client', 'Client', 2, 3)}
          {/* r4 (Top 1512): delivery_order (c1) · booking_no (c2) */}
          <Box sx={{ gridColumn: { sm: 1 }, gridRow: { sm: 4 } }}>
            <Combo label="DO" value={draft.deliveryOrder}
              options={deliveryOrders.data?.items ?? []} loading={deliveryOrders.isFetching} onInputChange={setDoQ}
              onPick={(v) => handleDeliveryOrderPick(v)} />
          </Box>
          {roField('bookingNo', 'Booking No', 2, 4)}
          {/* r5 (Top 1884): contract_no_of_bag (c1) · contract_quantity (c2) */}
          {roField('contractNoOfBag', 'No of bag', 1, 5, true)}
          {roField('contractQuantity', 'Quantity', 2, 5, true)}
        </Box>

        {/* ── General info (Access "Thông tin chung", Top 2292–3396) ── */}
        <Typography sx={sectionTitleSx}>General Information</Typography>
        <Box sx={grid2Sx}>
          {/* r1 (Top 2604): m_date (c1) · m_truck_no (c2) */}
          {dateField('mDate', 'M. Date', 1, 1)}
          {textField('mTruckNo', 'M. Truck No', 2, 1)}
          {/* r2 (Top 3024): from_warehouse (c1) · m_net_weight (c2) */}
          <Box sx={{ gridColumn: { sm: 1 }, gridRow: { sm: 2 } }}>
            <Combo label="From WH" value={draft.fromWarehouse}
              options={warehouses.data?.items ?? []} loading={warehouses.isFetching}
              onPick={(v) => set('fromWarehouse', v)} />
          </Box>
          {numField('mNetWeight', 'M. Net weight', 2, 2)}
          {/* r3 (Top 3396): to_warehouse (c1) */}
          <Box sx={{ gridColumn: { sm: 1 }, gridRow: { sm: 3 } }}>
            <Combo label="To WH" value={draft.toWarehouse}
              options={warehouses.data?.items ?? []} loading={warehouses.isFetching}
              onPick={(v) => set('toWarehouse', v)} />
          </Box>
        </Box>

        {/* ── VGM (Access "VGM", Top 3756–5100) ── */}
        <Typography sx={sectionTitleSx}>VGM</Typography>
        <Box sx={grid2Sx}>
          {textField('containerNo', 'Container No.', 1, 1)}
          {textField('sealNo', 'Seal No.', 2, 1, {
            error: sealExists,
            helperText: sealExists ? 'Seal No is existed!' : undefined,
            onBlur: () => checkSealNo(draft.sealNo),
          })}
          {numField('noBag', 'No Bag.', 1, 2)}
          {numField('tareWeight', 'Tare Weight', 2, 2, true)}
          {numField('tareCartonPallet', 'Tare carton and pallet', 1, 3, true)}
          {numField('tareCont', 'Tare Cont', 2, 3, true)}
        </Box>

        {/* ── Outbound (Access "Xuất Kho", Top 5328–6672) ── */}
        <Typography sx={sectionTitleSx}>Outbound</Typography>
        <Box sx={grid2Sx}>
          {numField('maxGrossWeight', 'Max Gross Weight', '1 / span 2', 1, true)}
          <Box sx={{ gridColumn: { sm: 1 }, gridRow: { sm: 2 } }}>
            <Combo label="Receipt No" value={draft.receiptNo} error={receiptBoth}
              options={receiptNos.data?.items ?? []} loading={receiptNos.isFetching}
              onPick={(v) => handleReceiptPick(v)} />
          </Box>
          <Box sx={{ gridColumn: { sm: 2 }, gridRow: { sm: 2 } }}>
            <Combo label="Virtual Warehouse" value={draft.virtualWh} error={receiptBoth}
              options={virtualWhs.data?.items ?? []} loading={virtualWhs.isFetching}
              onPick={(v) => handleVirtualWhPick(v)} />
          </Box>
          <Typography sx={{ ...hintSx, gridColumn: { sm: '1 / span 2' }, gridRow: { sm: 3 } }}>* Only choose Receipt No or Virtual Warehouse</Typography>
          {roField('exportNoOfBag', 'No of bag', 1, 4, true)}
          {roField('exportQuantity', 'Quantity', 2, 4, true)}
        </Box>

        {/* ── Customer inbound (Access "Nhập Kho Khách Hàng", Top 6876–7608) ── */}
        <Typography sx={sectionTitleSx}>Customer Inbound</Typography>
        <Box sx={grid2Sx}>
          {numField('netWeightAtBuyer', 'Net weight at buyer', 1, 1, true)}
          {numField('deductionAtBuyerKg', 'Deduction Kg', 2, 1)}
          {numField('deductionAtBuyerVnd', 'Deduction Vnd', '1 / span 2', 2)}
        </Box>

        {/* ── Allocation (Access "Phân Bổ Số Lượng", Top 8016–8748) ── */}
        <Typography sx={sectionTitleSx}>Allocation</Typography>
        <Box sx={grid2Sx}>
          {numField('allocatingBag', 'Allocating Bag', 1, 1)}
          {numField('allocatingQuantity', 'Allocating Quantity', 2, 1)}
          {textField('note', 'Note', 1, 2, { multiline: true, minRows: 2, sx: { gridColumn: { sm: '1 / span 2' }, gridRow: { sm: 2 } } })}
        </Box>
        </>
        )}
      </DialogContent>
    </Dialog>
  )
}
